import { StageEvent } from "../consts/STAGES";
import EnemyType from "../enums/EnemyType";
import StageEventType from "../enums/StageEventType";
import Enemy from "./enemy/Enemy";
import EnemyGroup from "./enemy/EnemyGroup";
import Game from "./Game";
import Stage from "./Stage";
import GameCore from "./GameCore";

export default class StageEventManager {
  scene: Phaser.Scene;
  stage: Stage;
  constructor(scene: Phaser.Scene, stage: Stage) {
    this.scene = scene;
    this.stage = stage;
  }
  TriggerEvent(e: StageEvent) {
    const eventType = e?.eventType;
    const delay = e?.delay;
    const chance = e?.chance;
    const repeat = e?.repeat;
    const duration = e?.duration;

    if (eventType) {
      Game.Core.scene.time.addEvent({
        delay: delay,
        loop: false,
        repeat: repeat,
        callback: () => {
          this.TriggerSwitchEvent(eventType, chance, duration);
        },
      });
    } else {
      this.TriggerSwitchEvent(eventType, chance, duration);
    }
  }
  TriggerSwitchEvent(
    eventType: StageEventType,
    chance: any,
    duration: number = 30000
  ) {
    const player = Game.Core.Player;
    const luck = player.luck || 1;
    if (!(chance && 100 * Math.random() > (chance * 1) / luck)) {
      switch (eventType) {
        case StageEventType.HAMSTER_SWARM:
          this.PlaySwarm(duration, EnemyType.HAMSTER_SWARM, 20);
          break;
        case StageEventType.SCORPION_SWARM:
          this.PlaySwarm(duration, EnemyType.SCORPION_SWARM, 20);
          break;
        case StageEventType.VULTURE_SWARM:
          this.PlaySwarm(duration, EnemyType.VULTURE_SWARM, 15);
          break;
        case StageEventType.CAMEL_CARAVAN:
          this.PlayCamelCaravan(3);
          break;
        case StageEventType.CHICKEN_SWARM:
          this.PlaySwarm(duration, EnemyType.CHICKEN_SWARM, 20);
          break;
        case StageEventType.SPIDER_SWARM:
          this.PlaySwarm(duration, EnemyType.SPIDER_SWARM, 20);
          break;
        case StageEventType.BEE_SWARM:
          this.PlaySwarm(duration, EnemyType.BEE_SWARM, 20);
          break;
        // case StageEventType.FAIRY_NPC:
        //   this.PlayNPC(duration, NPCType.FAIRY)
        // case StageEventType.VAMPRIE_NPC:
        //   this.PlayNPC(duration, NPCType.VAMPRIE)
        // case StageEventType.BUSINESSMAN_NPC:
        //   this.PlayNPC(duration, NPCType.BUSINESSMAN)
      }
    }
  }

  PlaySwarm(duration: number, enemyType: EnemyType, count: number) {
    let group = this.stage.pools.find((p) => p.enemyType === enemyType);

    if (!group) {
      group = new EnemyGroup(this.scene, enemyType);
      this.stage.pools.push(group);
      group.enabled = false;
    }

    let baseX = 2 * Math.PI * Math.random();
    const enemys: Enemy[] = [];

    for (let i = 0; i < count; i++) {
      const x =
        Game.Core.Player.x +
        0.8 * Math.cos(baseX) * (0.9 * this.scene.renderer.width);
      const y =
        Game.Core.Player.y +
        0.8 * Math.sin(baseX) * (0.9 * this.scene.renderer.height);

      baseX += Math.PI / 500;
      const enemy = group.SpawnAt(x, y);

      if (enemy) {
        enemy.isCullable = false;
        enemys.push(enemy);
      }
    }

    const checkAndDisappear = (enemies: Enemy[]) => {
      const remainingEnemies: Enemy[] = [];

      for (let i = 0, len = enemies.length; i < len; i++) {
        const e = enemies[i];
        if (!e.isDead) {
          if (e.isOutsideView) {
            e.Disappear();
          } else {
            remainingEnemies.push(e);
          }
        }
      }

      // If there are still enemies in view, check them again after duration
      if (remainingEnemies.length > 0) {
        Game.Core.scene.time.addEvent({
          delay: duration,
          loop: false,
          callback: () => checkAndDisappear(remainingEnemies),
        });
      }
    };

    Game.Core.scene.time.addEvent({
      delay: duration,
      loop: false,
      callback: () => checkAndDisappear(enemys),
    });
  }

  // Add this method to create the glow texture if it doesn't exist
  private createGlowTextureIfNeeded() {
    if (!this.scene.textures.exists("camel_glow_effect")) {
      const graphics = this.scene.make.graphics({});

      // Create a radial gradient for a golden/yellow glow
      const color = 0xffcc00; // Golden yellow

      // Draw circles with decreasing opacity for gradient effect
      graphics.fillStyle(color, 1);
      graphics.fillCircle(64, 64, 32);
      graphics.fillStyle(color, 0.6);
      graphics.fillCircle(64, 64, 48);
      graphics.fillStyle(color, 0.3);
      graphics.fillCircle(64, 64, 64);

      // Generate texture
      graphics.generateTexture("camel_glow_effect", 128, 128);
      graphics.destroy();
    }
  }

  // Add this method to create the glow effect
  private addGlowEffect(camel: Enemy) {
    // Create the texture if needed
    this.createGlowTextureIfNeeded();

    // Create a glow sprite that follows the camel
    const glow = this.scene.add.sprite(
      camel.x,
      camel.y - 20,
      "camel_glow_effect"
    ); // Position 20px higher

    // Use GameCore.PixelScale to adjust the size based on the game's global scale
    const baseScale = 1.0; // Smaller base scale
    glow.setScale(baseScale * GameCore.PixelScale);
    glow.setAlpha(0.7);
    glow.setBlendMode(Phaser.BlendModes.ADD);
    glow.setDepth(camel.depth - 1); // Place behind the camel

    // Add a pulsing effect that also respects the global scale
    this.scene.tweens.add({
      targets: glow,
      alpha: { from: 0.7, to: 0.3 },
      scale: {
        from: baseScale * GameCore.PixelScale,
        to: baseScale * 1.2 * GameCore.PixelScale,
      },
      duration: 800,
      yoyo: true,
      repeat: -1,
    });

    // Make the glow follow the camel but with vertical offset
    const updateListener = () => {
      if (camel && !camel.isDead && camel.active) {
        glow.setPosition(camel.x, camel.y - 20); // Maintain 20px higher position
      } else {
        glow.destroy();
        this.scene.events.off("update", updateListener);
      }
    };

    this.scene.events.on("update", updateListener);

    // Clean up when camel is destroyed or disappears
    camel.once("destroy", () => {
      glow.destroy();
      this.scene.events.off("update", updateListener);
    });
  }

  PlayCamelCaravan(camelCount: number = 3) {
    console.log("Spawning camel caravan");

    // Check if there are still caravan elements in the pool
    if (!Game.Core.StageService.hasCaravanElements()) {
      console.log("No more caravan elements available, skipping caravan spawn");
      return;
    }

    // Create a camel group if it doesn't exist
    let group = this.stage.pools.find((p) => p.enemyType === EnemyType.CAMEL);

    if (!group) {
      console.log("Creating new camel group");
      group = new EnemyGroup(this.scene, EnemyType.CAMEL);
      this.stage.pools.push(group);
      group.enabled = false;
    }

    // Get screen dimensions and player position
    const screenWidth = this.scene.cameras.main.width;
    const screenHeight = this.scene.cameras.main.height;
    const playerX = Game.Core.Player.x;
    const playerY = Game.Core.Player.y;

    // Choose one of four directions with some variation
    const baseDirections = [
      { x: 0, y: -1 }, // top to bottom
      { x: 1, y: 0 }, // right to left
      { x: 0, y: 1 }, // bottom to top
      { x: -1, y: 0 }, // left to right
    ];

    const dirIndex = Math.floor(Math.random() * 4);
    let direction = { ...baseDirections[dirIndex] };

    // Add some variation to the direction (not completely straight)
    if (direction.x === 0) {
      // For vertical movement, add some horizontal variation
      direction.x = (Math.random() - 0.5) * 0.5; // -0.25 to 0.25
    } else {
      // For horizontal movement, add some vertical variation
      direction.y = (Math.random() - 0.5) * 0.5; // -0.25 to 0.25
    }

    // Normalize the direction vector
    const dirLength = Math.sqrt(
      direction.x * direction.x + direction.y * direction.y
    );
    direction.x /= dirLength;
    direction.y /= dirLength;

    // Calculate start position (just outside screen but not too far)
    // Use a moderate offset to ensure camels start outside view but not too far
    const SPAWN_OFFSET = 200; // Distance beyond screen edge
    let startX, startY, endX, endY;

    if (Math.abs(direction.y) > Math.abs(direction.x)) {
      // More vertical than horizontal
      const sign = Math.sign(direction.y);
      startX = playerX + (Math.random() * screenWidth - screenWidth / 2) * 0.8;
      startY = playerY + sign * (screenHeight / 2 + SPAWN_OFFSET);

      // Set end point to be inside the visible area
      // This ensures camels will walk into view before stopping
      endX = startX;
      endY = playerY + sign * (screenHeight * 0.3); // 30% into the screen from the edge
    } else {
      // More horizontal than vertical
      const sign = Math.sign(direction.x);
      startX = playerX + sign * (screenWidth / 2 + SPAWN_OFFSET);
      startY =
        playerY + (Math.random() * screenHeight - screenHeight / 2) * 0.8;

      // Set end point to be inside the visible area
      endX = playerX + sign * (screenWidth * 0.3); // 30% into the screen from the edge
      endY = startY;
    }

    console.log(`Caravan path: (${startX},${startY}) to (${endX},${endY})`);
    console.log(`Player position: (${playerX}, ${playerY})`);
    console.log(`Screen dimensions: ${screenWidth}x${screenHeight}`);
    console.log(
      `Direction: (${direction.x.toFixed(2)}, ${direction.y.toFixed(2)})`
    );

    // Direction vector for the camels
    const dirX = endX - startX;
    const dirY = endY - startY;
    const length = Math.sqrt(dirX * dirX + dirY * dirY);
    const normalizedDirX = dirX / length;
    const normalizedDirY = dirY / length;

    const camels: Enemy[] = [];

    // Adjust camel count to not exceed the available caravan elements
    const availableElements =
      Game.Core.StageService.getCaravan()?.pool.length || 0;
    if (availableElements < camelCount) {
      camelCount = availableElements;
      console.log(
        `Adjusted camel count to ${camelCount} based on available caravan elements`
      );
    }

    // Spawn the camels in a line
    for (let i = 0; i < camelCount; i++) {
      try {
        // Take a caravan element from the pool for this camel
        const caravanElement = Game.Core.StageService.popCaravanElement();

        // Skip if no element available
        if (!caravanElement) {
          console.log("No more caravan elements available");
          break;
        }

        const offset = i * 150;
        const x = startX + normalizedDirX * offset;
        const y = startY + normalizedDirY * offset;

        // Use the standard SpawnAt method
        const camel = group.SpawnAt(x, y);

        if (camel) {
          // Store the caravan element data with the camel
          camel.setData("caravanElement", caravanElement);

          // Set fixed direction for the camel
          camel.FixedDirection = true;
          camel._currentDirection.x = normalizedDirX;
          camel._currentDirection.y = normalizedDirY;
          camel.isCullable = false;

          // Set a higher speed to ensure they cross the screen quickly
          camel.speed = 300;

          // Determine if this camel is special based on the element type
          const isSpecial = caravanElement.type === "special";

          // Mark special status in the camel's data
          camel.setData("isSpecialCamel", isSpecial);

          // If this is a special camel with specialValue, store that too
          if (isSpecial && caravanElement.specialValue) {
            camel.setData("specialValue", caravanElement.specialValue);
          }

          // Store initial direction for later use
          camel.setData("initialDirection", {
            x: normalizedDirX,
            y: normalizedDirY,
          });

          // Set camel state to "moving"
          camel.setData("state", "moving");

          // Only add glowing effect to the special camel
          if (isSpecial) {
            this.addGlowEffect(camel);
          }

          // Add event listener for damage
          camel.on("damage", () => {
            // If camel is idle and gets hit, make it flee
            if (camel.getData("state") === "idle") {
              // Calculate random flee direction (away from player with more randomness)
              const player = Game.Core.Player;
              const dx = player.x - camel.x;
              const dy = player.y - camel.y;

              // More randomness in flee angle when attacked
              const fleeAngle =
                Math.atan2(-dy, -dx) + (Math.random() - 0.5) * Math.PI;
              const fleeX = Math.cos(fleeAngle);
              const fleeY = Math.sin(fleeAngle);

              // Set to fleeing state
              camel.setData("state", "fleeing");
              camel.FixedDirection = true;
              camel._currentDirection.x = fleeX;
              camel._currentDirection.y = fleeY;
              camel.speed = FLEE_SPEED;

              // Restart animation
              camel.play("camel_run");
            }
          });

          camels.push(camel);

          console.log(
            `Spawned camel ${i} at (${x}, ${y})${
              isSpecial ? " (SPECIAL)" : ""
            }`,
            caravanElement
          );
        }
      } catch (error) {
        console.error("Error spawning camel:", error);
      }
    }

    // Define the distance at which camels react to the player
    const REACTION_DISTANCE = 200;
    const FLEE_SPEED = 350; // Slightly faster when fleeing

    // Add an update listener to handle camel behavior
    const updateCamelBehavior = () => {
      const player = Game.Core.Player;

      // Check if any camel in the caravan is damaged or killed
      const anyDamaged = camels.some(
        (camel) =>
          camel && camel.active && (camel.receivingDamage || camel.isDead)
      );

      // If any camel is damaged, make all idle camels flee
      if (anyDamaged) {
        for (let i = 0, len = camels.length; i < len; i++) {
          const camel = camels[i];
          if (
            camel &&
            camel.active &&
            !camel.isDead &&
            camel.getData("state") === "idle"
          ) {
            // Set to fleeing state
            camel.setData("state", "fleeing");

            // Calculate random flee direction (away from player with more randomness)
            const dx = player.x - camel.x;
            const dy = player.y - camel.y;

            // More randomness in flee angle when scared
            const fleeAngle =
              Math.atan2(-dy, -dx) + (Math.random() - 0.5) * Math.PI;
            const fleeX = Math.cos(fleeAngle);
            const fleeY = Math.sin(fleeAngle);

            // Set direction and speed
            camel.FixedDirection = true;
            camel._currentDirection.x = fleeX;
            camel._currentDirection.y = fleeY;
            camel.speed = FLEE_SPEED;

            // Restart animation
            camel.play("camel_run");
          }
        }
      }

      // Process individual camels
      for (let i = 0, len = camels.length; i < len; i++) {
        const camel = camels[i];
        if (camel && !camel.isDead && camel.active) {
          const state = camel.getData("state");

          // Check if camel is in view
          const isInView = !camel.isOutsideView;

          if (state === "moving" && isInView) {
            // Check if camel has moved far enough into the visible area
            // Calculate distance from screen edge
            const camera = Game.Core.scene.cameras.main;
            const viewBounds = camera.worldView;

            // Calculate how far the camel is from the edge of the screen
            let distanceFromEdge = 0;

            if (camel.x < viewBounds.left) {
              distanceFromEdge = viewBounds.left - camel.x;
            } else if (camel.x > viewBounds.right) {
              distanceFromEdge = camel.x - viewBounds.right;
            } else if (camel.y < viewBounds.top) {
              distanceFromEdge = viewBounds.top - camel.y;
            } else if (camel.y > viewBounds.bottom) {
              distanceFromEdge = camel.y - viewBounds.bottom;
            } else {
              // Camel is well inside the view - check if it's moved far enough in
              // Only stop if it's at least 20% into the screen
              const horizontalThreshold = viewBounds.width * 0.2;
              const verticalThreshold = viewBounds.height * 0.2;

              const leftEdgeDistance = camel.x - viewBounds.left;
              const rightEdgeDistance = viewBounds.right - camel.x;
              const topEdgeDistance = camel.y - viewBounds.top;
              const bottomEdgeDistance = viewBounds.bottom - camel.y;

              // If the camel is far enough from any edge, stop it
              if (
                leftEdgeDistance > horizontalThreshold &&
                rightEdgeDistance > horizontalThreshold &&
                topEdgeDistance > verticalThreshold &&
                bottomEdgeDistance > verticalThreshold
              ) {
                // Camel has entered the visible area and moved far enough in, stop and set to idle
                camel.setData("state", "idle");
                camel.FixedDirection = false;
                camel.speed = 0;

                camel.anims.stop();
                camel.setFrame(1);
              }
            }
          } else if (state === "idle" && !anyDamaged) {
            // Only check player proximity if no camels are damaged
            const dx = player.x - camel.x;
            const dy = player.y - camel.y;
            const distanceToPlayer = Math.sqrt(dx * dx + dy * dy);

            if (distanceToPlayer < REACTION_DISTANCE) {
              // Player is too close, camel starts fleeing
              camel.setData("state", "fleeing");

              // Calculate flee direction (away from player with some randomness)
              const fleeAngle =
                Math.atan2(-dy, -dx) + ((Math.random() - 0.5) * Math.PI) / 2;
              const fleeX = Math.cos(fleeAngle);
              const fleeY = Math.sin(fleeAngle);

              // Set new direction and speed
              camel.FixedDirection = true;
              camel._currentDirection.x = fleeX;
              camel._currentDirection.y = fleeY;
              camel.speed = FLEE_SPEED;

              // Restart the animation
              camel.play("camel_run");
            }
          }
        }
      }

      // Remove dead or inactive camels
      for (let i = camels.length - 1; i >= 0; i--) {
        if (!camels[i] || !camels[i].active || camels[i].isDead) {
          camels.splice(i, 1);
        }
      }

      // Continue updating as long as there are camels
      if (camels.length > 0) {
        Game.Core.scene.time.addEvent({
          delay: 100, // Check every 100ms
          callback: updateCamelBehavior,
        });
      }
    };

    // Start the behavior update loop
    Game.Core.scene.time.addEvent({
      delay: 100,
      callback: updateCamelBehavior,
    });

    // Use the same disappear logic as before, but only for camels that leave the screen
    const checkAndDisappear = (enemies: Enemy[]) => {
      const remainingEnemies: Enemy[] = [];

      for (let i = 0, len = enemies.length; i < len; i++) {
        const e = enemies[i];
        if (!e.isDead) {
          if (e.isOutsideView && e.getData("state") === "fleeing") {
            e.Disappear(); // Only disappear fleeing camels that are outside view
          } else {
            remainingEnemies.push(e);
          }
        }
      }

      if (remainingEnemies.length > 0) {
        Game.Core.scene.time.addEvent({
          delay: 1000,
          loop: false,
          callback: () => checkAndDisappear(remainingEnemies),
        });
      }
    };

    Game.Core.scene.time.addEvent({
      delay: 1000,
      loop: false,
      callback: () => checkAndDisappear(camels),
    });
  }

  // PlayNPC(duration: number = 10000, type: NPCType) {
  //   const npc = Game.Core.MakeNPC(type)
  //   Game.Core.scene.time.addEvent({
  //     delay: duration,
  //     loop: false,
  //     callback: () => {
  //       npc.Leave()
  //     }
  //   })
  // }
}
